import React, { useState, useRef, useEffect } from "react";
import { doc, updateDoc, arrayUnion, arrayRemove, collection, setDoc, query, where, onSnapshot } from "firebase/firestore";
import { firestore } from "../firebaseConfig";
import MemberAvatar from "./tasks/MemberAvatar";
import { parseDecisionMakers, computeHasEmail } from "../shared/enums";
import { suggestNewContactEmails, guessNameFromEmail } from "./notesContactSuggest";
import {
  TASK_ACTIONS,
  getTaskAction,
  nameFromEmail,
  firstName,
  formatDue,
  formatOpenSince,
  loadTeamMembers,
  taskAssigneeRefs,
  syncContactOpenAssignees,
  completeTask as completeTaskDoc,
} from "./tasks/taskUtils";
import {
  ACTIVITY_TYPES,
  CALL_OUTCOMES,
  SENTIMENTS,
  getActivityConfig,
  getOutcomeConfig,
  getSentimentConfig,
  getActivityPlaceholder,
  getNoteAuthor,
  normalizeLeadNotes,
  AiUseToggle,
} from "./NotesActivityFields";

const atTime = (date, h, m = 0) => {
  const d = new Date(date);
  d.setHours(h, m, 0, 0);
  return d.getTime();
};

const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d;
};

const nextWeekday = (weekday) => {
  const d = new Date();
  const diff = (weekday - d.getDay() + 7) % 7 || 7;
  d.setDate(d.getDate() + diff);
  return d;
};

/** Deadline shortcuts for team tasks, computed at click time. */
const DUE_PRESETS = [
  { id: "in_1h", label: "In 1 hour", icon: "fa-bolt", get: () => Date.now() + 60 * 60 * 1000 },
  { id: "eod", label: "End of today", icon: "fa-sun", get: () => atTime(new Date(), 18) },
  { id: "tomorrow_am", label: "Tomorrow morning", icon: "fa-mug-hot", get: () => atTime(addDays(1), 9) },
  { id: "tomorrow_eod", label: "Tomorrow EOD", icon: "fa-moon", get: () => atTime(addDays(1), 18) },
  { id: "eow", label: "End of week", icon: "fa-flag-checkered", get: () => atTime(addDays((5 - new Date().getDay() + 7) % 7), 17) },
  { id: "next_mon", label: "Next Monday", icon: "fa-calendar-week", get: () => atTime(nextWeekday(1), 9) },
];

const toDateTimeInputValue = (ts) => {
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const PERSON_FORM_EMPTY = { open: false, name: "", role: "", email: "", phone: "" };
const dismissedKey = (contactId) => `notesDismissedEmails_${contactId}`;

const lastAssigneeKey = (ownerId) => `notesTaskAssignees_${ownerId}`;

// Activity types / outcomes / sentiments are the single source of truth in
// NotesActivityFields so the Notes modal and the flow "Log Notes" step stay in sync.

/**
 * Reusable field set for logging / editing a private activity. Keeps the
 * composer and the inline edit view perfectly in sync.
 */
const PrivateActivityFields = ({
  activity, setActivity,
  outcome, setOutcome,
  sentiment, setSentiment,
  text, setText,
  excludeFromAi, setExcludeFromAi,
  inputRef, disabled, onSubmit, rows = 3,
}) => {
  const cfg = getActivityConfig(activity);
  return (
    <>
      <div className="notes-modal__activity-chips">
        {ACTIVITY_TYPES.map(a => (
          <button
            key={a.id}
            type="button"
            className={`notes-modal__activity-chip ${activity === a.id ? "notes-modal__activity-chip--active" : ""}`}
            onClick={() => { setActivity(a.id); setOutcome(null); }}
            disabled={disabled}
          >
            <i className={`fa ${a.icon}`}></i> {a.label}
          </button>
        ))}
      </div>

      {cfg.hasOutcome && (
        <div className="notes-modal__field">
          <label className="notes-modal__field-label">Call outcome</label>
          <div className="notes-modal__outcome-chips">
            {CALL_OUTCOMES.map(o => (
              <button
                key={o.id}
                type="button"
                className={`notes-modal__outcome-chip ${outcome === o.id ? `notes-modal__outcome-chip--active notes-modal__outcome-chip--${o.tone || "neutral"}` : ""}`}
                onClick={() => setOutcome(outcome === o.id ? null : o.id)}
                disabled={disabled}
              >
                <i className={`fa ${o.icon}`}></i> {o.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {cfg.hasSentiment && (
        <div className="notes-modal__field">
          <label className="notes-modal__field-label">How receptive was the contact?</label>
          <div className="notes-modal__sentiment">
            {SENTIMENTS.map(s => (
              <button
                key={s.id}
                type="button"
                className={`notes-modal__sentiment-btn notes-modal__sentiment-btn--${s.id} ${sentiment === s.id ? "notes-modal__sentiment-btn--active" : ""}`}
                onClick={() => setSentiment(sentiment === s.id ? null : s.id)}
                disabled={disabled}
              >
                <i className={`fa ${s.icon}`}></i>
                <span>{s.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <textarea
        ref={inputRef}
        className="notes-modal__input"
        placeholder={getActivityPlaceholder(activity)}
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onSubmit?.(); } }}
        rows={rows}
        disabled={disabled}
      />

      <AiUseToggle
        activity={activity}
        excludeFromAi={excludeFromAi}
        setExcludeFromAi={setExcludeFromAi}
        disabled={disabled}
      />
    </>
  );
};

const NotesModal = ({ isOpen, contact, onClose, userId, onContactUpdate, initialActivity }) => {
  const [activeTab, setActiveTab] = useState("private");
  const [saving, setSaving] = useState(false);
  const [localNotes, setLocalNotes] = useState([]);

  // Private composer state
  const [activity, setActivity] = useState("call");
  const [outcome, setOutcome] = useState(null);
  const [sentiment, setSentiment] = useState(null);
  const [privateText, setPrivateText] = useState("");
  const [excludeFromAi, setExcludeFromAi] = useState(false);

  // Team task (next step) composer state
  const [teamMembers, setTeamMembers] = useState([]);
  const [taskAssignees, setTaskAssignees] = useState([]);
  const [openTasks, setOpenTasks] = useState([]);
  const [taskAction, setTaskAction] = useState(null);
  const [taskMessage, setTaskMessage] = useState("");
  const [taskDue, setTaskDue] = useState(null);
  const [taskDuePreset, setTaskDuePreset] = useState(null);
  const [duePickerOpen, setDuePickerOpen] = useState(false);
  const duePickerRef = useRef(null);

  // People at this account (lead_decision_makers) + add-person form
  const [people, setPeople] = useState([]);
  const [personForm, setPersonForm] = useState(PERSON_FORM_EMPTY);
  const [personSaving, setPersonSaving] = useState(false);
  const [dismissedEmails, setDismissedEmails] = useState([]);

  // Outreach composer state
  const [outreachText, setOutreachText] = useState("");

  // Whether the "add new note" composer is expanded (only relevant once
  // notes already exist — before that it's always shown in full).
  const [composerOpen, setComposerOpen] = useState(false);

  // Inline edit state
  const [editingId, setEditingId] = useState(null);
  const [editActivity, setEditActivity] = useState("call");
  const [editOutcome, setEditOutcome] = useState(null);
  const [editSentiment, setEditSentiment] = useState(null);
  const [editText, setEditText] = useState("");
  const [editExcludeFromAi, setEditExcludeFromAi] = useState(false);

  const privateInputRef = useRef(null);
  const outreachInputRef = useRef(null);

  const resetComposer = () => {
    setActivity("call");
    setOutcome(null);
    setSentiment(null);
    setPrivateText("");
    setExcludeFromAi(false);
    setOutreachText("");
    setTaskAction(null);
    setTaskMessage("");
    setTaskDue(null);
    setTaskDuePreset(null);
    setDuePickerOpen(false);
    setComposerOpen(false);
  };

  const applyDuePreset = (preset) => {
    setTaskDue(preset.get());
    setTaskDuePreset(preset.id);
    setDuePickerOpen(false);
  };

  const applyDueCustom = (value) => {
    if (!value) { setTaskDue(null); setTaskDuePreset(null); return; }
    const ts = new Date(value).getTime();
    if (Number.isNaN(ts)) return;
    setTaskDue(ts);
    setTaskDuePreset("custom");
  };

  const applyTaskAction = (action) => {
    if (taskAction === action.id) {
      setTaskAction(null);
      if (taskMessage === action.defaultMessage) setTaskMessage("");
      return;
    }
    const prevDefault = getTaskAction(taskAction)?.defaultMessage;
    if (!taskMessage.trim() || taskMessage === prevDefault) setTaskMessage(action.defaultMessage);
    setTaskAction(action.id);
  };

  const toggleAssignee = (uid) => {
    setTaskAssignees(prev => {
      if (prev.includes(uid) && prev.length === 1) return prev;
      const next = prev.includes(uid) ? prev.filter(u => u !== uid) : [...prev, uid];
      try { localStorage.setItem(lastAssigneeKey(userId), JSON.stringify(next)); } catch (_) { /* storage unavailable */ }
      return next;
    });
  };

  const completeTask = async (task) => {
    try {
      const uids = await completeTaskDoc(userId, task);
      if (uids) onContactUpdate(contact.id, { open_task_assignees: uids });
    } catch (error) {
      console.error("Error completing task:", error);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditActivity("call");
    setEditOutcome(null);
    setEditSentiment(null);
    setEditText("");
    setEditExcludeFromAi(false);
  };

  useEffect(() => {
    if (isOpen && contact) {
      setLocalNotes(normalizeLeadNotes(contact.lead_notes));
      setPeople(parseDecisionMakers(contact));
      setPersonForm(PERSON_FORM_EMPTY);
      try { setDismissedEmails(JSON.parse(localStorage.getItem(dismissedKey(contact.id)) || "[]")); } catch (_) { setDismissedEmails([]); }
      setActiveTab("private");
      resetComposer();
      if (initialActivity) {
        setActivity(initialActivity);
        setComposerOpen(true);
      }
      cancelEdit();
      setTimeout(() => privateInputRef.current?.focus(), 150);
    }
  }, [isOpen, contact, initialActivity]);

  useEffect(() => {
    if (!isOpen || !userId) return undefined;
    let cancelled = false;
    loadTeamMembers(userId)
      .then(members => {
        if (cancelled) return;
        setTeamMembers(members);
        let last = [];
        try { last = JSON.parse(localStorage.getItem(lastAssigneeKey(userId)) || "[]"); } catch (_) { /* storage unavailable */ }
        const valid = (Array.isArray(last) ? last : []).filter(uid => members.some(m => m.uid === uid));
        const selfUid = members.find(m => m.isSelf)?.uid;
        setTaskAssignees(valid.length ? valid : (selfUid ? [selfUid] : [members[0]?.uid].filter(Boolean)));
      })
      .catch(err => console.error("Error loading team members:", err));
    return () => { cancelled = true; };
  }, [isOpen, userId]);

  useEffect(() => {
    if (!isOpen || !userId || !contact?.id) return undefined;
    const q = query(
      collection(firestore, `World/${userId}/tasks`),
      where("contactId", "==", contact.id),
      where("status", "==", "open"),
    );
    const unsub = onSnapshot(
      q,
      snap => setOpenTasks(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))),
      err => console.error("Error loading open tasks:", err),
    );
    return unsub;
  }, [isOpen, userId, contact?.id]);

  useEffect(() => {
    if (!duePickerOpen) return undefined;
    const onDown = (e) => { if (duePickerRef.current && !duePickerRef.current.contains(e.target)) setDuePickerOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); setDuePickerOpen(false); } };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [duePickerOpen]);

  // Lock background scroll while the modal is open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, [isOpen]);

  if (!isOpen || !contact) return null;

  const contactRef = () => doc(firestore, `World/${userId}/db/${contact.id}`);

  const persistNotes = (updatedNotes) => {
    setLocalNotes(updatedNotes);
    onContactUpdate(contact.id, { lead_notes: updatedNotes, updated_at: Date.now() });
  };

  const emailSuggestion = suggestNewContactEmails(
    { ...contact, lead_decision_makers: people },
    [privateText, outreachText, ...[...localNotes].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)).map(n => n.text || "")],
    dismissedEmails,
  )[0] || null;

  const dismissEmail = (email) => {
    const next = [...dismissedEmails, email];
    setDismissedEmails(next);
    try { localStorage.setItem(dismissedKey(contact.id), JSON.stringify(next)); } catch (_) { /* storage unavailable */ }
  };

  const openPersonForm = (email = "") => {
    setPersonForm({ open: true, name: guessNameFromEmail(email), role: "", email, phone: "" });
  };

  const savePerson = async () => {
    const name = personForm.name.trim();
    const email = personForm.email.trim().toLowerCase();
    if ((!name && !email) || !userId) return;
    setPersonSaving(true);
    try {
      const newPerson = {
        name: name || guessNameFromEmail(email) || email,
        role: personForm.role.trim() || null,
        email: email || null,
        phone: personForm.phone.trim() || null,
        socials: null,
        addedFrom: "notes",
        addedAt: new Date().toISOString(),
      };
      const updated = [...people, newPerson];
      const payload = { lead_decision_makers: JSON.stringify(updated) };
      payload.has_email = computeHasEmail({ ...contact, ...payload });
      await updateDoc(contactRef(), payload);
      setPeople(updated);
      onContactUpdate(contact.id, payload);
      setPersonForm(PERSON_FORM_EMPTY);
    } catch (error) {
      console.error("Error adding contact person:", error);
    }
    setPersonSaving(false);
  };

  const selectedAssignees = teamMembers.filter(m => taskAssignees.includes(m.uid));
  const hasTask = selectedAssignees.length > 0 && (!!taskAction || taskMessage.trim().length > 0);

  const memberByUid = (uid) => teamMembers.find(m => m.uid === uid);

  const createTask = async (relatedActivity, noteId) => {
    if (!selectedAssignees.length) return null;
    const assignees = selectedAssignees.map(m => ({ uid: m.uid, email: m.email || null, name: m.name }));
    const taskRef = doc(collection(firestore, `World/${userId}/tasks`));
    const author = getNoteAuthor();
    await setDoc(taskRef, {
      contactId: contact.id,
      contactName: contact.business_name || "Contact",
      action: taskAction || "other",
      message: taskMessage.trim() || getTaskAction(taskAction)?.label || "",
      assigneeUids: assignees.map(a => a.uid),
      assignees,
      assignedByUid: author.createdByUid,
      assignedByEmail: author.createdByEmail,
      activity: relatedActivity || null,
      noteId: noteId || null,
      dueAt: taskDue || null,
      status: "open",
      createdAt: Date.now(),
    });
    try {
      const uids = await syncContactOpenAssignees(userId, contact.id);
      onContactUpdate(contact.id, { open_task_assignees: uids });
    } catch (error) {
      console.error("Error flagging contact with open task:", error);
    }
    return {
      id: taskRef.id,
      dueAt: taskDue || null,
      action: taskAction || "other",
      message: taskMessage.trim() || getTaskAction(taskAction)?.label || "",
      assigneeUids: assignees.map(a => a.uid),
      assignees,
    };
  };

  const handleAddPrivate = async () => {
    const hasActivityContent = activity === "note"
      ? privateText.trim().length > 0
      : (privateText.trim().length > 0 || !!outcome || !!sentiment);
    if ((!hasActivityContent && !hasTask) || !userId) return;
    setSaving(true);
    try {
      const noteId = `note_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      const task = hasTask ? await createTask(activity, hasActivityContent ? noteId : null) : null;
      if (hasActivityContent) {
        const newNote = {
          id: noteId,
          type: "private",
          activity,
          outcome: outcome || null,
          sentiment: sentiment || null,
          text: privateText.trim(),
          ...(excludeFromAi ? { excludeFromAi: true } : {}),
          createdAt: Date.now(),
          ...getNoteAuthor(),
          ...(task ? { task } : {}),
        };
        await updateDoc(contactRef(), { lead_notes: arrayUnion(newNote), updated_at: Date.now() });
        persistNotes([...localNotes, newNote]);
      }
      resetComposer();
      setTimeout(() => privateInputRef.current?.focus(), 50);
    } catch (error) {
      console.error("Error adding activity:", error);
    }
    setSaving(false);
  };

  const handleAddOutreach = async () => {
    if (!outreachText.trim() || !userId) return;
    setSaving(true);
    try {
      const newNote = {
        id: `note_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        type: "outreach",
        text: outreachText.trim(),
        createdAt: Date.now(),
        ...getNoteAuthor(),
      };
      await updateDoc(contactRef(), { lead_notes: arrayUnion(newNote), updated_at: Date.now() });
      persistNotes([...localNotes, newNote]);
      setOutreachText("");
      setComposerOpen(false);
    } catch (error) {
      console.error("Error adding note:", error);
    }
    setSaving(false);
  };

  const handleDelete = async (noteToDelete) => {
    if (!userId) return;
    try {
      await updateDoc(contactRef(), { lead_notes: arrayRemove(noteToDelete), updated_at: Date.now() });
      persistNotes(localNotes.filter(n => n.id !== noteToDelete.id));
    } catch (error) {
      console.error("Error deleting note:", error);
    }
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditText(note.text || "");
    if ((note.type || "private") === "outreach") return;
    setEditActivity(note.activity || "note");
    setEditOutcome(note.outcome || null);
    setEditSentiment(note.sentiment || null);
    setEditExcludeFromAi(note.excludeFromAi === true);
  };

  const handleSaveEdit = async (oldNote) => {
    if (!userId) return;
    const isOutreach = (oldNote.type || "private") === "outreach";
    if (isOutreach && !editText.trim()) return;
    try {
      const updatedNote = isOutreach
        ? { ...oldNote, text: editText.trim(), editedAt: Date.now() }
        : {
            ...oldNote,
            activity: editActivity,
            outcome: editOutcome || null,
            sentiment: editSentiment || null,
            text: editText.trim(),
            excludeFromAi: editExcludeFromAi,
            editedAt: Date.now(),
          };
      await updateDoc(contactRef(), { lead_notes: arrayRemove(oldNote) });
      await updateDoc(contactRef(), { lead_notes: arrayUnion(updatedNote), updated_at: Date.now() });
      persistNotes(localNotes.map(n => n.id === oldNote.id ? updatedNote : n));
      cancelEdit();
    } catch (error) {
      console.error("Error editing note:", error);
    }
  };

  const privateCanSave = (activity === "note"
    ? privateText.trim().length > 0
    : (privateText.trim().length > 0 || !!outcome || !!sentiment)) || hasTask;

  const visibleNotes = [...localNotes]
    .filter(n => (activeTab === "outreach" ? (n.type === "outreach") : (n.type !== "outreach")))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  const privateCount = localNotes.filter(n => n.type !== "outreach").length;
  const outreachCount = localNotes.filter(n => n.type === "outreach").length;

  const taskAssigneeList = (task) => {
    return taskAssigneeRefs(task).map(a => memberByUid(a.uid) || { uid: a.uid, name: a.name || nameFromEmail(a.email) || a.email || "Team member" });
  };

  const taskAssigneeNames = (task) =>
    taskAssigneeList(task).map(m => (m.isSelf ? "You" : firstName(m.name))).join(", ") || "Team member";

  const renderPrivateItem = (note) => {
    const activityCfg = getActivityConfig(note.activity || "note");
    const outcomeCfg = getOutcomeConfig(note.outcome);
    const sentimentCfg = getSentimentConfig(note.sentiment);
    return (
      <>
        <div className="notes-modal__item-top notes-modal__item-top--activity">
          <span className={`notes-modal__activity-badge notes-modal__activity-badge--${activityCfg.id}`}>
            <i className={`fa ${activityCfg.icon}`}></i> {activityCfg.label}
          </span>
          {outcomeCfg && (
            <span className={`notes-modal__outcome-badge notes-modal__outcome-badge--${outcomeCfg.tone || "neutral"}`}>
              <i className={`fa ${outcomeCfg.icon}`}></i> {outcomeCfg.label}
            </span>
          )}
          {sentimentCfg && (
            <span className={`notes-modal__sentiment-badge notes-modal__sentiment-badge--${sentimentCfg.id}`}>
              <i className={`fa ${sentimentCfg.icon}`}></i> {sentimentCfg.label}
            </span>
          )}
          {note.excludeFromAi === true && (
            <span className="notes-modal__ai-excluded-badge" title="Not used as call context in AI emails">
              <i className="fa fa-eye-slash"></i> Not for AI
            </span>
          )}
        </div>
        {note.text && <p className="notes-modal__text">{note.text}</p>}
        {note.task && (
          <div className="notes-modal__task-badge">
            <i className={`fa ${getTaskAction(note.task.action)?.icon || "fa-list-check"}`}></i>
            <span>
              <strong>{taskAssigneeNames(note.task)}</strong>
              {note.task.message ? `: ${note.task.message}` : ""}
            </span>
            {note.task.dueAt && (
              <span className="notes-modal__task-badge-due"><i className="fa fa-clock"></i> {formatDue(note.task.dueAt)}</span>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="notes-modal-overlay" onClick={onClose}>
      <div className="notes-modal" onClick={e => e.stopPropagation()}>
        <div className="notes-modal__header">
          <h3><i className="fa fa-sticky-note"></i> Notes – {contact.business_name || "Contact"}</h3>
          <button className="notes-modal__close" onClick={onClose}><i className="fa fa-times"></i></button>
        </div>

        <div className="notes-modal__tabs">
          <button
            className={`notes-modal__tab ${activeTab === "private" ? "notes-modal__tab--active" : ""}`}
            onClick={() => { setActiveTab("private"); cancelEdit(); setComposerOpen(false); }}
          >
            <i className="fa fa-lock"></i> Private
            {privateCount > 0 && <span className="notes-modal__tab-count">{privateCount}</span>}
          </button>
          <button
            className={`notes-modal__tab ${activeTab === "outreach" ? "notes-modal__tab--active notes-modal__tab--outreach" : ""}`}
            onClick={() => { setActiveTab("outreach"); cancelEdit(); setComposerOpen(false); }}
          >
            <i className="fa fa-bullhorn"></i> Outreach Info
            {outreachCount > 0 && <span className="notes-modal__tab-count">{outreachCount}</span>}
          </button>
        </div>

        <div className="notes-modal__body">
        {openTasks.length > 0 && (
          <div className="notes-modal__open-tasks">
            {openTasks.map(t => {
              const act = getTaskAction(t.action);
              const people = taskAssigneeList(t);
              const overdue = t.dueAt && t.dueAt < Date.now();
              const by = memberByUid(t.assignedByUid);
              return (
                <div key={t.id} className={`notes-modal__open-task ${overdue ? "notes-modal__open-task--overdue" : ""}`}>
                  <span className="notes-modal__open-task-pulse" />
                  <span className="notes-modal__avatar-stack">
                    {people.slice(0, 3).map(m => <MemberAvatar key={m.uid} member={m} size={28} />)}
                  </span>
                  <div className="notes-modal__open-task-body">
                    <div className="notes-modal__open-task-title">
                      <i className={`fa ${act?.icon || "fa-list-check"}`}></i>
                      <strong>{people.map(m => (m.isSelf ? "You" : firstName(m.name))).join(", ")}</strong>
                      <span>· {act?.label || "Task"}</span>
                    </div>
                    {t.message && <div className="notes-modal__open-task-msg">{t.message}</div>}
                    <div className="notes-modal__open-task-meta">
                      <span><i className="fa fa-hourglass-half"></i> Open for {formatOpenSince(t.createdAt)}</span>
                      {t.dueAt && <span className={overdue ? "is-overdue" : ""}><i className="fa fa-clock"></i> {overdue ? "Overdue · " : "Due "}{formatDue(t.dueAt)}</span>}
                      {(by || t.assignedByEmail) && <span><i className="fa fa-user"></i> by {by ? (by.isSelf ? "you" : firstName(by.name)) : t.assignedByEmail}</span>}
                    </div>
                  </div>
                  <button type="button" className="notes-modal__open-task-done" onClick={() => completeTask(t)} title="Mark as done">
                    <i className="fa fa-check"></i> Done
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <div className="notes-modal__people-strip">
          <div className="notes-modal__people-row">
            <span className="notes-modal__people-label"><i className="fa fa-address-book"></i> People</span>
            {people.filter(p => p && (p.name || p.email)).map((p, i) => (
              <span key={`${p.email || p.name}-${i}`} className="notes-modal__person-chip" title={[p.role, p.email, p.phone].filter(Boolean).join(" · ")}>
                <MemberAvatar member={{ uid: p.email || p.name, name: p.name || p.email }} size={20} />
                {p.name || p.email}
                {p.role && <em>{p.role}</em>}
              </span>
            ))}
            {!personForm.open && (
              <button type="button" className="notes-modal__person-add" onClick={() => openPersonForm()}>
                <i className="fa fa-user-plus"></i> Add person
              </button>
            )}
          </div>

          {emailSuggestion && !personForm.open && (
            <div className="notes-modal__suggest">
              <span className="notes-modal__suggest-icon"><i className="fa fa-wand-magic-sparkles"></i></span>
              <span className="notes-modal__suggest-text">
                Found <strong>{emailSuggestion}</strong> in your notes. Add as a new contact person?
              </span>
              <button type="button" className="notes-modal__suggest-yes" onClick={() => openPersonForm(emailSuggestion)}>
                <i className="fa fa-user-plus"></i> Add
              </button>
              <button type="button" className="notes-modal__suggest-no" onClick={() => dismissEmail(emailSuggestion)} title="Don't suggest this address again">
                Not now
              </button>
            </div>
          )}

          {personForm.open && (
            <form className="notes-modal__person-form" onSubmit={e => { e.preventDefault(); savePerson(); }}>
              <div className="notes-modal__person-grid">
                <input autoFocus placeholder="Name" value={personForm.name} onChange={e => setPersonForm(f => ({ ...f, name: e.target.value }))} />
                <input placeholder="Role (e.g. Head of Purchasing)" value={personForm.role} onChange={e => setPersonForm(f => ({ ...f, role: e.target.value }))} />
                <input type="email" placeholder="Email" value={personForm.email} onChange={e => setPersonForm(f => ({ ...f, email: e.target.value }))} />
                <input placeholder="Phone" value={personForm.phone} onChange={e => setPersonForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
              <div className="notes-modal__person-actions">
                <button type="button" className="notes-modal__edit-cancel" onClick={() => setPersonForm(PERSON_FORM_EMPTY)}>Cancel</button>
                <button type="submit" className="notes-modal__edit-save" disabled={personSaving || (!personForm.name.trim() && !personForm.email.trim())}>
                  {personSaving ? <i className="fa fa-spinner fa-spin"></i> : <i className="fa fa-check"></i>} Save person
                </button>
              </div>
            </form>
          )}
        </div>

        <div className={`notes-modal__add-section ${visibleNotes.length > 0 ? "notes-modal__add-section--divided" : ""}`}>
          {visibleNotes.length > 0 && (
            <button type="button" className="notes-modal__add-toggle" onClick={() => setComposerOpen(o => !o)}>
              <span><i className="fa fa-plus"></i> {activeTab === "private" ? "Add note" : "Add outreach info"}</span>
              <i className={`fa fa-chevron-${composerOpen ? "up" : "down"}`}></i>
            </button>
          )}
          {(visibleNotes.length === 0 || composerOpen) && (
          <div className="notes-modal__form">
          {activeTab === "private" ? (
            <>
              <PrivateActivityFields
                activity={activity} setActivity={setActivity}
                outcome={outcome} setOutcome={setOutcome}
                sentiment={sentiment} setSentiment={setSentiment}
                text={privateText} setText={setPrivateText}
                excludeFromAi={excludeFromAi} setExcludeFromAi={setExcludeFromAi}
                inputRef={privateInputRef}
                disabled={saving}
                onSubmit={handleAddPrivate}
              />

              {teamMembers.length > 0 && (
                <div className="notes-modal__task">
                  <div className="notes-modal__task-head">
                    <span className="notes-modal__task-title"><i className="fa fa-rocket"></i> Who takes the next step?</span>
                    <span className="notes-modal__task-sub">Pick one or more teammates, keep the deal rolling</span>
                  </div>
                  <div className="notes-modal__people">
                    {teamMembers.map(m => {
                      const active = taskAssignees.includes(m.uid);
                      return (
                        <button
                          key={m.uid}
                          type="button"
                          className={`notes-modal__person ${active ? "notes-modal__person--active" : ""}`}
                          onClick={() => toggleAssignee(m.uid)}
                          disabled={saving}
                          title={m.email || m.name}
                        >
                          <span className="notes-modal__person-pic">
                            <MemberAvatar member={m} size={42} />
                            {active && <span className="notes-modal__person-check"><i className="fa fa-check"></i></span>}
                          </span>
                          <span className="notes-modal__person-name">{m.isSelf ? "Me" : firstName(m.name)}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="notes-modal__missions">
                    {TASK_ACTIONS.map(a => (
                      <button
                        key={a.id}
                        type="button"
                        className={`notes-modal__mission notes-modal__mission--${a.id} ${taskAction === a.id ? "notes-modal__mission--active" : ""}`}
                        onClick={() => applyTaskAction(a)}
                        disabled={saving}
                      >
                        <span className="notes-modal__mission-icon"><i className={`fa ${a.icon}`}></i></span>
                        <span className="notes-modal__mission-label">{a.label}</span>
                        <span className="notes-modal__mission-tag">{a.tagline}</span>
                      </button>
                    ))}
                  </div>
                  {hasTask && (() => {
                    const act = getTaskAction(taskAction);
                    const solo = selectedAssignees.length === 1 ? selectedAssignees[0] : null;
                    const who = solo
                      ? (solo.isSelf ? "You're" : `${firstName(solo.name)} is`)
                      : `${selectedAssignees.map(m => (m.isSelf ? "You" : firstName(m.name))).join(" & ")} are`;
                    return (
                      <div className="notes-modal__task-summary">
                        <span className="notes-modal__avatar-stack">
                          {selectedAssignees.slice(0, 3).map(m => <MemberAvatar key={m.uid} member={m} size={22} />)}
                        </span>
                        <span>
                          <strong>{who} on it</strong>
                          {act ? ` · ${act.label}` : ""}
                          {taskDue ? ` · ${formatDue(taskDue)}` : ""}
                        </span>
                        <i className="fa fa-bolt"></i>
                      </div>
                    );
                  })()}
                  {(taskAction || taskMessage) && (
                    <div className="notes-modal__task-row">
                      <textarea
                        rows={3}
                        className="notes-modal__reminder-note notes-modal__task-message"
                        placeholder="Message for your teammate — what exactly should happen, what's important…"
                        value={taskMessage}
                        onChange={e => setTaskMessage(e.target.value)}
                        disabled={saving}
                      />
                      <div className="notes-modal__due" ref={duePickerRef}>
                        <button
                          type="button"
                          className={`notes-modal__due-btn ${taskDue ? "notes-modal__due-btn--set" : ""} ${taskDue && taskDue < Date.now() ? "notes-modal__due-btn--past" : ""}`}
                          onClick={() => setDuePickerOpen(o => !o)}
                          disabled={saving}
                        >
                          <i className="fa fa-clock"></i> {taskDue ? formatDue(taskDue) : "Due by…"}
                        </button>
                        {duePickerOpen && (
                          <div className="notes-modal__due-pop">
                            <div className="notes-modal__due-pop-title">Due by</div>
                            <div className="notes-modal__due-presets">
                              {DUE_PRESETS.map(p => (
                                <button
                                  key={p.id}
                                  type="button"
                                  className={`notes-modal__due-preset ${taskDuePreset === p.id ? "notes-modal__due-preset--active" : ""}`}
                                  onClick={() => applyDuePreset(p)}
                                >
                                  <i className={`fa ${p.icon}`}></i>
                                  <span>{p.label}</span>
                                  <small>{formatDue(p.get())}</small>
                                </button>
                              ))}
                            </div>
                            <label className="notes-modal__due-custom">
                              <span>Pick date & time</span>
                              <input
                                type="datetime-local"
                                value={taskDue && taskDuePreset === "custom" ? toDateTimeInputValue(taskDue) : ""}
                                min={toDateTimeInputValue(Date.now())}
                                onChange={e => applyDueCustom(e.target.value)}
                              />
                            </label>
                            {taskDue && (
                              <button
                                type="button"
                                className="notes-modal__reminder-clear"
                                onClick={() => { setTaskDue(null); setTaskDuePreset(null); setDuePickerOpen(false); }}
                              >
                                <i className="fa fa-times"></i> No deadline
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="notes-modal__task-clear"
                        onClick={() => { setTaskAction(null); setTaskMessage(""); setTaskDue(null); setTaskDuePreset(null); }}
                        title="Clear task"
                      >
                        <i className="fa fa-times"></i>
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="notes-modal__form-actions">
                <span className="notes-modal__hint"><i className="fa fa-keyboard"></i> ⌘/Ctrl + Enter</span>
                <button className="notes-modal__add" onClick={handleAddPrivate} disabled={!privateCanSave || saving}>
                  {saving ? <i className="fa fa-spinner fa-spin"></i> : <><i className="fa fa-plus"></i> Save Note</>}
                </button>
              </div>
            </>
          ) : (
            <>
              <textarea
                ref={outreachInputRef}
                className="notes-modal__input"
                placeholder="Outreach info the AI should use in emails…"
                value={outreachText}
                onChange={e => setOutreachText(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAddOutreach(); } }}
                rows={3}
                disabled={saving}
              />
              <div className="notes-modal__form-actions">
                <span className="notes-modal__hint"><i className="fa fa-robot"></i> Used by AI email writer</span>
                <button className="notes-modal__add notes-modal__add--outreach" onClick={handleAddOutreach} disabled={!outreachText.trim() || saving}>
                  {saving ? <i className="fa fa-spinner fa-spin"></i> : <><i className="fa fa-plus"></i> Add</>}
                </button>
              </div>
            </>
          )}
          </div>
          )}
        </div>

        {visibleNotes.length > 0 && (
        <div className="notes-modal__list">
          {visibleNotes.map(note => {
            const isOutreach = (note.type || "private") === "outreach";
            const isEditing = editingId === note.id;
            return (
              <div key={note.id} className={`notes-modal__item ${isEditing ? "notes-modal__item--editing" : ""} ${isOutreach ? "notes-modal__item--outreach" : ""}`}>
                {isEditing ? (
                  <div className="notes-modal__edit">
                    {isOutreach ? (
                      <textarea
                        className="notes-modal__edit-input"
                        value={editText}
                        onChange={e => setEditText(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSaveEdit(note); } if (e.key === "Escape") cancelEdit(); }}
                        rows={2}
                        autoFocus
                      />
                    ) : (
                      <PrivateActivityFields
                        activity={editActivity} setActivity={setEditActivity}
                        outcome={editOutcome} setOutcome={setEditOutcome}
                        sentiment={editSentiment} setSentiment={setEditSentiment}
                        text={editText} setText={setEditText}
                        excludeFromAi={editExcludeFromAi} setExcludeFromAi={setEditExcludeFromAi}
                        disabled={false}
                        onSubmit={() => handleSaveEdit(note)}
                        rows={2}
                      />
                    )}
                    <div className="notes-modal__edit-actions">
                      <button className="notes-modal__edit-cancel" onClick={cancelEdit}>Cancel</button>
                      <button className="notes-modal__edit-save" onClick={() => handleSaveEdit(note)} disabled={isOutreach && !editText.trim()}>
                        <i className="fa fa-check"></i> Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {isOutreach ? (
                      <>
                        <div className="notes-modal__item-top">
                          <span className="note-type-badge note-type-badge--outreach">
                            <i className="fa fa-bullhorn"></i> Outreach Info
                          </span>
                        </div>
                        <p className="notes-modal__text">{note.text}</p>
                      </>
                    ) : (
                      renderPrivateItem(note)
                    )}
                    <div className="notes-modal__meta">
                      <span className="notes-modal__date">
                        {new Date(note.editedAt || note.createdAt).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                        {note.editedAt ? " (edited)" : ""}
                      </span>
                      <div className="notes-modal__item-actions">
                        <button onClick={() => startEdit(note)} title="Edit"><i className="far fa-edit"></i></button>
                        <button onClick={() => handleDelete(note)} title="Delete"><i className="far fa-trash-alt"></i></button>
                      </div>
                    </div>
                    {(note.source === "flow" || note.createdByEmail) && (
                      <div className="notes-modal__provenance">
                        {note.source === "flow" && (
                          <span className="notes-modal__prov-flow" title={note.flowName ? `Added via flow: ${note.flowName}` : "Added via flow"}>
                            <i className="fa fa-diagram-project"></i> {note.flowName || "Flow"}
                          </span>
                        )}
                        {note.createdByEmail && (
                          <span className="notes-modal__prov-author" title={`Saved by ${note.createdByEmail}`}>
                            <i className="fa fa-user"></i> {note.createdByEmail}
                          </span>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default NotesModal;
